import React, { useState } from 'react';
import { Button, FieldGroup, ButtonGroup } from '@ver-uds/react';
import { Alert } from '@ver-uds/uswds-react';
import { Link, useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { Account, AccountStatus, AccountType, BatchClosedAccountInfoRequest } from 'eve-account-client';
import { EveRoutes } from '@ver-uds/e-verify';
import FullWidthLayout from '../layouts/FullWidthLayout';
import { useCloseBatchAccountMutation, useClosedReasonsQuery } from '../services';
import AlertConstants from '../constants/AlertConstants';
import { redirectToUrl } from '../utils/util';
import { getVerbiageForTerminationRequest } from '../utils/termination-request';
import ErrorConstants from '../constants/ErrorConstants';
import ErrorBar from './ErrorBar';
import SelectedCompanyLocationsAccordion from './ManageCompanies/SelectedCompanyLocationsAccordion';
import BulkTerminationRequestSummary from './TerminationRequestSummary/BulkTerminationRequestSummary';
import HelpTextConstants from '../constants/HelpTextConstants';
import TerminationRequestSummary from './TerminationRequestSummary/TerminationRequestSummary';

type TerminateCompanyLocationsReviewProps = {
  accountType: AccountType;
  accountData: Account;
  selectedAccounts: Array<Account>;
  terminationFormValues: FormValues;
  leaveReviewPage: () => void;
};

type FormValues = {
  closedReasonCode: string;
  closedReasonText: string;
  childClosedReasonCode: string;
};

const pageTitle = 'Review Termination Request';

const TerminateCompanyLocationsReview = ({
  accountType,
  accountData,
  selectedAccounts,
  terminationFormValues,
  leaveReviewPage,
}: TerminateCompanyLocationsReviewProps): JSX.Element => {
  const [showTerminateCompanyLocationsSummaryPage, setShowTerminateCompanyLocationsSummaryPage] = useState(
    selectedAccounts[0]?.status === AccountStatus.TERMINATIONREQUESTED,
  );

  const parentAccountId = accountData.id as string;

  const { data: closedReasons } = useClosedReasonsQuery(accountData?.type);

  const history = useHistory();

  const todayDatePretty = format(new Date(), 'E, MMM d, yyyy');

  const { companyPronoun, companyText, possessiveCompanyText } = getVerbiageForTerminationRequest(
    selectedAccounts.length,
  );

  const alertHeading = AlertConstants.TERMINATION_REQUEST_CONFIRM_HEADING.replace(
    /{{ companyPronoun }}/g,
    companyPronoun,
  ).replace('{{ possessiveCompanyText }}', possessiveCompanyText);
  const alertText = AlertConstants.TERMINATION_REQUEST_CONFIRM_TEXT.replace(
    /{{ companyPronoun }}/g,
    companyPronoun,
  ).replace('{{ companyText }}', companyText);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const closedRequestReasonCode =
    terminationFormValues?.childClosedReasonCode ?? terminationFormValues?.closedReasonCode;
  const closedReasonConfirmationDisplay =
    terminationFormValues?.closedReasonText ||
    closedReasons?.find((f) => f.code === closedRequestReasonCode)?.displayName;

  const [mutate] = useCloseBatchAccountMutation((error: Error | Response) => {
    setErrorMessage(ErrorConstants.GENERAL_ERROR);
  });

  let manageCompaniesLink: string;
  let manageCompaniesLinkText: string;
  let headerTitle: string;

  if (accountType === AccountType.AGENCY) {
    manageCompaniesLink = EveRoutes.COMPANY_CLIENTS_VIEW;
    manageCompaniesLinkText = 'Manage Client Companies';
    headerTitle = 'Client Companies';
  } else {
    manageCompaniesLink = EveRoutes.CORPORATE_COMPANY_LOCATIONS_VIEW;
    manageCompaniesLinkText = 'Manage Company Locations';
    headerTitle = 'Company Locations';
  }

  const renderTerminationRequestSummary = () => {
    if (selectedAccounts.length === 1) {
      return <TerminationRequestSummary accountData={selectedAccounts[0]} accountType={accountType} />;
    }

    const dtDateOnly = new Date();
    const description = HelpTextConstants.TERMINATION_COMPANY_LOCATIONS_SUMMARY.replace(
      '{{ terminationRequestDate }}',
      format(dtDateOnly, 'MMMM d, yyyy'),
    );

    return (
      <BulkTerminationRequestSummary
        selectedAccounts={selectedAccounts}
        accountType={accountType}
        title="Termination Request Summary"
        description={
          <>
            {description} To cancel or edit a request for a company, go to{' '}
            <Link to={manageCompaniesLink}>{manageCompaniesLinkText}</Link> to manage termination requests.
          </>
        }
      />
    );
  };

  const onSubmit = async () => {
    const batchClosedAccountInfoRequestData: Array<BatchClosedAccountInfoRequest> = [];
    selectedAccounts.forEach((account) => {
      batchClosedAccountInfoRequestData.push({
        status: AccountStatus.TERMINATIONREQUESTED,
        closedRequestReasonText: terminationFormValues?.closedReasonText ?? undefined,
        closedRequestReasonCode,
        accountId: account.id as string,
      });
    });

    const responseObj = await mutate(
      {
        accountId: parentAccountId,
        batchClosedAccountInfoRequest: batchClosedAccountInfoRequestData,
      },
      {},
    );

    if (responseObj) {
      responseObj.forEach((batchTerminationInfo) => {
        const account = selectedAccounts.find(
          (selectedAccount) => selectedAccount.id === batchTerminationInfo.accountId,
        );

        if (account) {
          account.closedDate = batchTerminationInfo.closedDate;
          account.closedRequestDate = new Date();
          account.closedRequestReason = batchTerminationInfo.closedReason;
        }
      });
    }
  };

  const onFormSubmit = async () => {
    try {
      await onSubmit();
      setShowTerminateCompanyLocationsSummaryPage(true);
    } catch (error) {
      setErrorMessage(ErrorConstants.GENERAL_ERROR);
    }
  };

  return showTerminateCompanyLocationsSummaryPage ? (
    renderTerminationRequestSummary()
  ) : (
    <FullWidthLayout title={pageTitle} headerTitle={headerTitle}>
      <section className="padding-y-4 margin-bottom-3">
        <h1>{pageTitle}</h1>
        <ErrorBar errorMessage={errorMessage} />
        <Alert status="warning">
          <Alert.Heading>{alertHeading}</Alert.Heading>
          <Alert.Text>{alertText}</Alert.Text>
        </Alert>
        <FieldGroup className="margin-top-3">
          <div className="margin-bottom-3">
            <SelectedCompanyLocationsAccordion selectedAccounts={selectedAccounts} />
          </div>
          <FieldGroup.Field className="tablet:grid-col-12" name="Termination Request Date">
            {todayDatePretty}
          </FieldGroup.Field>
          <FieldGroup.Field className="tablet:grid-col-12" name="Termination Request Reason">
            {closedReasonConfirmationDisplay}
          </FieldGroup.Field>
        </FieldGroup>
        <hr className="margin-top-5 margin-bottom-4" />
        <ButtonGroup>
          <Button type="button" variant="secondary" onClick={leaveReviewPage}>
            Back
          </Button>
          <Button type="button" variant="destructive" onClick={onFormSubmit}>
            Yes, Request Termination
          </Button>
          <Button type="button" variant="tertiary" onClick={() => redirectToUrl(history, manageCompaniesLink)}>
            Cancel
          </Button>
        </ButtonGroup>
      </section>
    </FullWidthLayout>
  );
};

export default TerminateCompanyLocationsReview;
